/**
 * usePresignedDocumentUrls — bulk presigned-URL hook for document references.
 *
 * Decisions implemented (see .planning/phases/04-.../04-CONTEXT.md):
 *   D-01  Eager fetch on mount (single bulk mutation per message)
 *   D-02  Bulk mutation `documentUrls(documentIds)` returns FileDownloadResponse[]
 *   D-05  Hybrid refresh: setTimeout ~2 min before soonest expiry +
 *         visibilitychange refetch when cached entry is older than staleness threshold
 *   D-06  IntersectionObserver pauses the refresh timer while observed element
 *         is offscreen; resume refetches immediately if cached entry is stale
 *   D-07  isError flips when fetch fails — consumer renders fallback path
 *   D-08  isLoading reflects the initial fetch only
 */

import * as React from "react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useMutation } from "@apollo/client/react";
import { gql } from "@apollo/client";

export interface PresignedUrlEntry {
    /** Pre-signed URL, ready to use as anchor href. */
    url: string;
    /** ms-epoch absolute time when this URL stops working. Server-provided. */
    expiresAt: number;
}

export interface UsePresignedDocumentUrlsOptions {
    /**
     * DOM element observed for IntersectionObserver pause/resume (D-06).
     * When omitted, refresh runs unconditionally while the hook is mounted.
     */
    observedElementRef?: React.RefObject<HTMLElement | null>;
    /** ms before expiry to pre-emptively refetch. Default 2 min (D-05). */
    refreshLeadTimeMs?: number;
    /**
     * On visibilitychange => 'visible', refetch if cached entry is older than
     * this many ms. Default 10 min (D-05).
     */
    visibilityStalenessMs?: number;
}

export interface UsePresignedDocumentUrlsResult {
    /** Map keyed by document ID. Missing keys = use fallback path. */
    urls: Map<string, PresignedUrlEntry>;
    /** True only during the initial fetch; refresh fetches do not flip this. */
    isLoading: boolean;
    /** True if the most recent fetch failed and `urls` is empty. */
    isError: boolean;
    /** Manual refresh trigger. */
    refresh: () => void;
}

const DEFAULT_REFRESH_LEAD_TIME_MS = 2 * 60 * 1000; // 2 min — D-05
const DEFAULT_VISIBILITY_STALENESS_MS = 10 * 60 * 1000; // 10 min — D-05

const GET_DOCUMENT_URLS_BULK = gql`
    mutation GetDocumentUrlsBulk($documentIds: [UUID!]!) {
        documentUrls(documentIds: $documentIds) {
            id
            filename
            presignedUrl
            expiresAt
        }
    }
`;

interface DocumentUrlsBulkData {
    documentUrls: Array<{
        id: string;
        filename: string;
        presignedUrl: string;
        // ISO string from Strawberry DateTime!
        expiresAt: string;
    }>;
}

interface DocumentUrlsBulkVars {
    documentIds: string[];
}

/**
 * Detect whether the current runtime exposes a working visibilitychange API.
 * `document` is undefined under SSR; `visibilityState` may also be undefined
 * in some embedded webviews. Both are guarded.
 */
function hasVisibilityApi(): boolean {
    return typeof document !== "undefined" && typeof document.visibilityState !== "undefined";
}

export function usePresignedDocumentUrls(
    documentIds: string[],
    options?: UsePresignedDocumentUrlsOptions,
): UsePresignedDocumentUrlsResult {
    const refreshLeadTimeMs = options?.refreshLeadTimeMs ?? DEFAULT_REFRESH_LEAD_TIME_MS;
    const visibilityStalenessMs = options?.visibilityStalenessMs ?? DEFAULT_VISIBILITY_STALENESS_MS;
    const observedElementRef = options?.observedElementRef;

    // Stable, sorted, deduped key derived from documentIds. Apollo MockedProvider
    // matches mocks by exact variable equality, so ordering matters in tests AND
    // canonical ordering lets Apollo coalesce calls with the same set.
    const idsKey = useMemo(() => {
        const unique = Array.from(new Set(documentIds));
        unique.sort();
        return unique.join("|");
    }, [documentIds]);

    const sortedIds = useMemo(() => (idsKey === "" ? [] : idsKey.split("|")), [idsKey]);

    const [urls, setUrls] = useState<Map<string, PresignedUrlEntry>>(new Map());
    const [isLoading, setIsLoading] = useState<boolean>(sortedIds.length > 0);
    const [isError, setIsError] = useState<boolean>(false);

    const [getUrlsBulk] = useMutation<DocumentUrlsBulkData, DocumentUrlsBulkVars>(
        GET_DOCUMENT_URLS_BULK,
        // Mutations don't read cache by default, but explicit fetchPolicy
        // documents intent and is robust against Apollo defaults changing.
        { fetchPolicy: "no-cache" },
    );

    // Refs that callbacks need but should not retrigger renders when changed.
    const isMountedRef = useRef<boolean>(true);
    const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const lastFetchAtRef = useRef<number>(0);
    const isOnscreenRef = useRef<boolean>(true); // viewport visibility (D-06)
    const urlsRef = useRef<Map<string, PresignedUrlEntry>>(urls);
    const refreshLeadTimeRef = useRef<number>(refreshLeadTimeMs);
    const visibilityStalenessRef = useRef<number>(visibilityStalenessMs);

    // Keep refs up-to-date so async callbacks read current values without
    // adding render dependencies.
    useEffect(() => {
        urlsRef.current = urls;
    }, [urls]);
    useEffect(() => {
        refreshLeadTimeRef.current = refreshLeadTimeMs;
    }, [refreshLeadTimeMs]);
    useEffect(() => {
        visibilityStalenessRef.current = visibilityStalenessMs;
    }, [visibilityStalenessMs]);

    // Mount/unmount guard (Pitfall 3 — useEffect cleanup races with refetch).
    useEffect(() => {
        isMountedRef.current = true;
        return () => {
            isMountedRef.current = false;
        };
    }, []);

    const clearTimer = useCallback(() => {
        if (timerRef.current !== null) {
            clearTimeout(timerRef.current);
            timerRef.current = null;
        }
    }, []);

    const scheduleRefresh = useCallback(
        (entries: Map<string, PresignedUrlEntry>, fetchFn: () => void) => {
            clearTimer();
            if (entries.size === 0) return;
            if (!isOnscreenRef.current) return; // paused by IntersectionObserver
            let minExpiresAt = Infinity;
            for (const e of entries.values()) {
                if (e.expiresAt < minExpiresAt) minExpiresAt = e.expiresAt;
            }
            if (!Number.isFinite(minExpiresAt)) return;
            const delay = Math.max(0, minExpiresAt - refreshLeadTimeRef.current - Date.now());
            timerRef.current = setTimeout(() => {
                timerRef.current = null;
                fetchFn();
            }, delay);
        },
        [clearTimer],
    );

    // Fetch implementation. Stable identity within a single idsKey lifetime
    // so effects depending on it don't refire on every render.
    const doFetchRef = useRef<() => Promise<void>>(async () => {});
    doFetchRef.current = async () => {
        if (sortedIds.length === 0) {
            if (isMountedRef.current) {
                setIsLoading(false);
                setIsError(false);
            }
            return;
        }
        try {
            const response = await getUrlsBulk({ variables: { documentIds: sortedIds } });
            if (!isMountedRef.current) return;
            const data = response.data;
            if (!data) {
                throw new Error("documentUrls: empty response");
            }
            const next = new Map<string, PresignedUrlEntry>();
            for (const item of data.documentUrls) {
                const expiresAtMs = new Date(item.expiresAt).getTime();
                next.set(item.id, {
                    url: item.presignedUrl,
                    expiresAt: expiresAtMs,
                });
            }
            setUrls(next);
            urlsRef.current = next;
            setIsLoading(false);
            setIsError(false);
            lastFetchAtRef.current = Date.now();
            scheduleRefresh(next, () => {
                void doFetchRef.current();
            });
        } catch {
            if (!isMountedRef.current) return;
            setIsLoading(false);
            setIsError(true);
        }
    };

    // Initial fetch on mount + whenever the canonical id set changes.
    useEffect(() => {
        if (sortedIds.length === 0) {
            setUrls(new Map());
            urlsRef.current = new Map();
            setIsLoading(false);
            setIsError(false);
            clearTimer();
            return;
        }
        setIsLoading(true);
        setIsError(false);
        void doFetchRef.current();
        return () => {
            clearTimer();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [idsKey]);

    // visibilitychange listener (D-05).
    useEffect(() => {
        if (!hasVisibilityApi()) return;
        const handler = () => {
            const isVisible = document.visibilityState !== "hidden";
            if (!isVisible) return;
            if (lastFetchAtRef.current <= 0) return;
            const stale = Date.now() - lastFetchAtRef.current > visibilityStalenessRef.current;
            if (stale) void doFetchRef.current();
        };
        document.addEventListener("visibilitychange", handler);
        return () => {
            document.removeEventListener("visibilitychange", handler);
        };
    }, []);

    // IntersectionObserver pause/resume (D-06).
    useEffect(() => {
        if (typeof IntersectionObserver === "undefined") return;
        const el = observedElementRef?.current;
        if (!el) return;
        const observer = new IntersectionObserver((entries) => {
            const entry = entries[entries.length - 1];
            const isIntersecting = entry?.isIntersecting ?? true;
            isOnscreenRef.current = isIntersecting;
            if (!isIntersecting) {
                clearTimer();
                return;
            }
            const cached = urlsRef.current;
            if (cached.size === 0) return;
            const now = Date.now();
            const stale =
                lastFetchAtRef.current > 0 &&
                now - lastFetchAtRef.current > visibilityStalenessRef.current;
            if (stale) {
                void doFetchRef.current();
            } else {
                scheduleRefresh(cached, () => {
                    void doFetchRef.current();
                });
            }
        });
        observer.observe(el);
        return () => {
            observer.disconnect();
        };
    }, [observedElementRef, scheduleRefresh, clearTimer]);

    const refresh = useCallback(() => {
        void doFetchRef.current();
    }, []);

    return { urls, isLoading, isError, refresh };
}
